import {
  EventFormInputType,
  EventRequestState,
  type EventFragment,
  type EventTicketApprovalRequestInput,
} from "~/__generated__/graphql/graphql";
import type { CheckoutDraft } from "./checkout-state";

type ApprovalFormInputs = EventFragment["formInputs"];
type ApprovalRequest = EventFragment["myRequest"];
type ApprovalForm = NonNullable<EventTicketApprovalRequestInput["form"]>;
type StoredFormAnswer = NonNullable<NonNullable<ApprovalRequest>["form"]>[number];

export function serializeApprovalAnswers(
  inputs: ApprovalFormInputs,
  answers: CheckoutDraft["answers"],
): ApprovalForm {
  return inputs.map(input => ({
    value: input.type === EventFormInputType.Text ? answers[input.id]?.text.trim() ?? "" : (answers[input.id]?.selected ?? []).filter(value => input.selectOptions.includes(value)).join(", "),
    formQuestionID: input.id, selectedValues: input.type === EventFormInputType.Select ? (answers[input.id]?.selected ?? []).filter(value => input.selectOptions.includes(value)) : undefined,
    formQuestionLabel: input.label, formQuestionType: input.type, formQuestionOptions: [...input.selectOptions],
  }));
}

function requestFallbackForm(request: ApprovalRequest | null | undefined): ApprovalForm {
  const resumable =
    request?.state === EventRequestState.Pending && !request.submittedAt
      ? request
      : null;
  return (resumable?.form ?? []).map(answer => ({
    value: answer.value,
    formQuestionLabel: answer.formQuestionLabel,
    formQuestionType: answer.formQuestionType,
    formQuestionOptions: [...answer.formQuestionOptions],
  }));
}

// A stored answer is only reusable while it still satisfies the current input:
// the API validates submissions against the current form, so a renamed
// question or edited select options make a stored snapshot unsubmittable.
function storedAnswerMatchesInput(
  input: ApprovalFormInputs[number],
  answer: StoredFormAnswer | undefined,
): boolean {
  if (!answer?.value || answer.formQuestionLabel.trim() !== input.label) return false;
  if (input.type !== EventFormInputType.Select) return true;
  const options = input.selectOptions;
  if (answer.formQuestionOptions.length !== options.length ||
    !answer.formQuestionOptions.every((option, index) => option === options[index])) return false;
  const selected = answer.selectedValues ?? answer.value.split(", ");
  return selected.length > 0 && selected.every(value => options.includes(value));
}

function storedFormComplete(
  inputs: ApprovalFormInputs,
  request: ApprovalRequest | null | undefined,
): boolean {
  if (request?.state !== EventRequestState.Pending || request.submittedAt) return false;
  const form = request.form ?? [];
  return inputs.every(input => storedAnswerMatchesInput(input, form.find(answer =>
    (answer.formQuestionID ? answer.formQuestionID === input.id : answer.formQuestionLabel === input.label) &&
    answer.formQuestionType === input.type)));
}

// Prefer complete draft answers; fall back to answers stored on the resumable
// request so resuming checkout does not submit blank answers.
export function approvalRequestForm(
  inputs: ApprovalFormInputs,
  answers: CheckoutDraft["answers"],
  request: ApprovalRequest | null | undefined,
): ApprovalForm {
  const draftForm = serializeApprovalAnswers(inputs, answers);
  if (draftForm.length && draftForm.every(answer => answer.value)) return draftForm;
  return storedFormComplete(inputs, request) ? requestFallbackForm(request) : [];
}

// Payment may only render once every host question has an answer, either in the
// draft or already stored on the resumable request.
export function approvalAnswersComplete(
  inputs: ApprovalFormInputs,
  answers: CheckoutDraft["answers"],
  request: ApprovalRequest | null | undefined,
): boolean {
  if (!inputs.length) return true;
  if (serializeApprovalAnswers(inputs, answers).every(answer => answer.value)) return true;
  return storedFormComplete(inputs, request);
}
